import { useCallback, useEffect, useState } from 'react'
import { Siren, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchEmergency,
  createEmergency,
  updateStatus,
  deleteEmergency,
  URGENCIES,
  urgencyMeta,
  statusMeta,
  EM_STR as S,
  type EmergencyRequest,
} from './emergencyApi'

export function EmergencyScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<EmergencyRequest[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [agency, setAgency] = useState('')
  const [officer, setOfficer] = useState('')
  const [legalBasis, setLegalBasis] = useState('')
  const [urgency, setUrgency] = useState('high')
  const [subjectRef, setSubjectRef] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchEmergency(status, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const create = async () => {
    if (!agency.trim() && !description.trim()) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createEmergency({ agency: agency.trim(), officer: officer.trim(), contact: '', legal_basis: legalBasis.trim(), urgency, subject_ref: subjectRef.trim(), description: description.trim() })
      setAgency('')
      setOfficer('')
      setLegalBasis('')
      setSubjectRef('')
      setDescription('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const setStat = async (id: number, st: string) => {
    let note = ''
    let disclosed = ''
    if (st === 'disclosed') disclosed = window.prompt(S.disclosed) ?? ''
    if (st === 'disclosed' || st === 'denied') note = window.prompt(S.description) ?? ''
    try {
      await updateStatus(id, st, note, disclosed)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteEmergency(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A19.05" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create */}
          <div className="space-y-2 rounded-lg border border-danger/40 bg-danger/5 p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-danger">
              <Siren size={15} />
              {S.create}
            </div>
            <div className="flex flex-wrap gap-2">
              <input value={agency} onChange={(e) => setAgency(e.target.value)} placeholder={S.agency} className="w-40 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <input value={officer} onChange={(e) => setOfficer(e.target.value)} placeholder={S.officer} className="w-36 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
                {URGENCIES.map((u) => (
                  <option key={u.key} value={u.key}>{u.label}</option>
                ))}
              </select>
              <input value={subjectRef} onChange={(e) => setSubjectRef(e.target.value)} placeholder={S.subjectRef} className="w-40 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
            </div>
            <div className="flex flex-wrap gap-2">
              <input value={legalBasis} onChange={(e) => setLegalBasis(e.target.value)} placeholder={S.legalBasis} className="w-48 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={S.description} className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <button type="button" onClick={create} disabled={busy} className="rounded-md bg-danger px-4 py-2 text-sm text-white disabled:opacity-50">
                {S.add}
              </button>
            </div>
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="received">received</option>
            <option value="reviewing">reviewing</option>
            <option value="disclosed">disclosed</option>
            <option value="denied">denied</option>
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => {
                const u = urgencyMeta(r.urgency)
                const st = statusMeta(r.status)
                return (
                  <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-primary">{r.agency || '—'}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${u.pill}`}>{u.label}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${st.pill}`}>{st.label}</span>
                        </div>
                        {r.officer && <p className="mt-1 text-xs text-tertiary">{r.officer}{r.legal_basis && ` · ${r.legal_basis}`}</p>}
                        {r.subject && <p className="text-sm text-secondary">{S.subject}: <span className="text-primary">{r.subject}</span></p>}
                        {r.description && <p className="text-xs text-tertiary">{r.description}</p>}
                        {r.disclosed && <p className="text-xs text-success">✓ {S.disclosed}: {r.disclosed}</p>}
                        {r.response_note && <p className="text-xs text-tertiary">→ {r.response_note}</p>}
                      </div>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                        {r.status !== 'disclosed' && r.status !== 'denied' && (
                          <>
                            {r.status === 'received' && (
                              <button type="button" onClick={() => setStat(r.id, 'reviewing')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                                {S.reviewing}
                              </button>
                            )}
                            <button type="button" onClick={() => setStat(r.id, 'disclosed')} className="rounded-md bg-success px-2 py-1 text-xs text-white">
                              {S.disclose}
                            </button>
                            <button type="button" onClick={() => setStat(r.id, 'denied')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                              {S.deny}
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
